import { Fragment, useEffect } from 'react';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  matchPath,
  useSearchParams,
  Navigate
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { TCountry } from '../types/outerLayout/common';
import { LOCAL_STORAGE_KEYS } from '../constants/local-storage-keys';
import { COUNTRIES } from '@app/dashboard/constants/countries';
import { SEARCH_PARAMS_KEYS } from '../constants/search-params-keys';
import { COOKIES_KEYS } from '../constants/cookies-keys';
import { QUERY_KEYS } from '../constants/query-keys';
import { validateSession } from '../tools/axios/axiosUtils';
import { useNavigationStore } from '../store/navigation';
import { useGlobalStore } from '../store/global';
import { useLangStore } from '../store/lang';
import { getOldDashboardUserInfo } from '../services/switch-dashboard-interface';
import { getCountryFromDomain } from '../utils/getCountryFromDomain';
import { getCookie, removeCookie } from '../utils/manageCookies';
import { handleAfterLogin } from '../views/pages/outer_layout/Login/utils';
import {
  addToLocalStorage,
  deleteFromLocalStorage,
  getFromLocalStorage,
  handleAuthLogout
} from '../utils/manageLocalStorage';
import { OUTER_ROUTES, outerLayoutRoutes } from './outer-routes';
import { INNER_ROUTES, innerLayoutRoutes } from './inner-routes';
import OuterLayout from '../views/layout/OuterLayout';
import InnerLayout from '../views/layout/InnerLayout';
import { FullPageLoader } from '../components/shared/FullPageLoader';
import { PAYMOB_PRO_DEEP_LINK } from '../constants/paymob-pro';

const country: TCountry = getCountryFromDomain();

let isAutoLoggedIn = false;

const { DEEP_LINK } = LOCAL_STORAGE_KEYS;

export const Router = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const { isAuthenticated, setIsAuthenticated } = useGlobalStore();
  const { setLang, initLang } = useLangStore();
  const { setActiveLink } = useNavigationStore();

  const isInnerRoute = innerLayoutRoutes.some(route => matchPath({ path: route.path, end: true }, location.pathname));
  const isOuterRoute = outerLayoutRoutes.some(route => matchPath({ path: route.path, end: true }, location.pathname));
  
  const FEATURES_DEEP_LINKS = [
    {
      route: PAYMOB_PRO_DEEP_LINK,
      canAccess: () => getFromLocalStorage(LOCAL_STORAGE_KEYS.IS_PAYMOB_PRO_ENABLED),
      routeState: 'openPaymobPro',
      actionSite: INNER_ROUTES.HOME
    }
  ];

  const setDeepLink = () => {
    const { pathname, search } = location;
    const route = `${pathname}${search}`;

    !isOuterRoute && pathname !== '/' && addToLocalStorage(DEEP_LINK, route);
  };

  const isRouteAllowed = (
    permissions?: { list: string[]; exact?: boolean },
    routeName?: string,
    countriesList?: TCountry[],
    specialCondition?: () => boolean,
  ) => {
    const userPermissions = getFromLocalStorage(LOCAL_STORAGE_KEYS.PERMISSIONS);
    const { list: permissionsList, exact } = permissions || {};
    const isAllowedCountry = countriesList?.includes(country) ?? true;
    const isSpecialCondition = specialCondition?.() ?? true;

    if (routeName && INNER_ROUTES.REPORTS?.includes(routeName)) {
      const hasStatements = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_STATEMENTS)?.enabled || false;
      const hasDigest = getFromLocalStorage(LOCAL_STORAGE_KEYS.DIGEST)?.enabled || false;
      return permissionsList?.some((perm) => userPermissions?.includes(perm)) || hasStatements || hasDigest;
    }

    const hasPermission = permissionsList ?
      exact ?
        permissionsList.every((perm) => userPermissions?.includes(perm))
        :
        permissionsList.some((perm) => userPermissions?.includes(perm))
      :
      true;

    return hasPermission && isAllowedCountry && isSpecialCondition;
  };

  const sdi = searchParams.get(SEARCH_PARAMS_KEYS.SWITCH_DASHBOARDS_INTERFACE);
  const switchDashboardsToken = getCookie(COOKIES_KEYS.SWITCH_DASHBOARDS_TOKEN);
  const switchDashboardsLang = getCookie(COOKIES_KEYS.SWITCH_DASHBOARDS_LANGUAGE) as 'en' | 'ar' || 'en';

  const {
    data: oldDashboardUserInfo,
  } = useQuery({
    queryKey: [QUERY_KEYS.OLD_DASHBOARD_USER_INFO],
    queryFn: () => getOldDashboardUserInfo(),
    enabled: !!(sdi && switchDashboardsToken)
  });

  const handleSwitchAutoLogin = async () => {

    if (oldDashboardUserInfo) {
      await handleAfterLogin(oldDashboardUserInfo, navigate);
      setLang(switchDashboardsLang);

      searchParams.delete(SEARCH_PARAMS_KEYS.SWITCH_DASHBOARDS_INTERFACE);
      setSearchParams(searchParams);

      removeCookie(COOKIES_KEYS.SWITCH_DASHBOARDS_TOKEN);
      removeCookie(COOKIES_KEYS.SWITCH_DASHBOARDS_LANGUAGE);

      isAutoLoggedIn = true;
      setIsAuthenticated(true);
    };
  };

   useEffect(() => {
    const deepLink = getFromLocalStorage(LOCAL_STORAGE_KEYS.DEEP_LINK);
    const normalizedDeepLink = deepLink?.replace(/\/+$/, '');
    const normalizedLocationPathname = location.pathname.replace(/\/+$/, '');

    if (sdi && switchDashboardsToken) {
      setIsAuthenticated('pending');
    } else {
      //⚠️⚠️
      // "${search}" most probably has no effect on any feature 
      // maybe it's implemented like this to just keep the route as is (with it's search params) without actual need for this search params preservation,
      // but keeping it for now in case of any issues

      if (isAuthenticated === true) {
        if (!isInnerRoute) {
          // const route = `${deepLink || INNER_ROUTES.HOME}${search}`;
          const featDeepLink = FEATURES_DEEP_LINKS.find((featureDeepLink) => (normalizedDeepLink || normalizedLocationPathname) === featureDeepLink.route);
          const route = featDeepLink ? featDeepLink.actionSite : normalizedDeepLink || INNER_ROUTES.HOME;

          // const route = deepLink || INNER_ROUTES.HOME;
          navigate(
            route,
            {
              replace: true,
              state: featDeepLink?.canAccess() ? { [featDeepLink.routeState]: true } : undefined,
            },
          );

          normalizedDeepLink && deleteFromLocalStorage(LOCAL_STORAGE_KEYS.DEEP_LINK);
        }
      } else if (isAuthenticated === false) {
        if (!isOuterRoute) {
          // navigate(`${OUTER_ROUTES.LOGIN}${search}`, { replace: true });

          navigate(`${OUTER_ROUTES.LOGIN}`, { replace: true });
          setActiveLink({ nav: "", sub: "" });
        }
      }
    }
  }, [location.pathname, isAuthenticated]);

  useEffect(() => {
    // Switch dashboards auto login
    !isAutoLoggedIn && handleSwitchAutoLogin();
  }, [location, oldDashboardUserInfo]);

  useEffect(() => {
    initLang()
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuth = await validateSession();
      setIsAuthenticated(!!isAuth);
      if (!isAuth) {
        handleAuthLogout();
        setDeepLink();
      }
    };

    verifyAuth();
  }, []);

  return (
    <>
      {
        isAuthenticated === true &&
        <InnerLayout>
          <Routes>
            {
              innerLayoutRoutes.map((route) =>
                isRouteAllowed(route.permissions, route.name, route.allowedCountries, route.specialCondition) ?
                  <Route path={route.path} element={route.element} key={route.name} />
                  :
                  <Fragment key={route.name}></Fragment>
              )
            }
            <Route path='*' element={<Navigate to={INNER_ROUTES.HOME} />} />
          </Routes>
        </InnerLayout>
      }
      {
        isAuthenticated === false &&
        <OuterLayout>
          <Routes>
            {outerLayoutRoutes.map((route) => {
              // to check that leads register route is only available in Egypt
              return (route.name === "leads_register" && country !== COUNTRIES.EGY) ?
                <Fragment key={route.name}></Fragment>
                :
                <Route path={route.path} element={route.element} key={route.name} />
            })}
          </Routes>
        </OuterLayout>
      }
      {
        isAuthenticated === 'pending' &&
        <FullPageLoader />
      }
    </>
  )
}
